import { useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { FC, useMemo, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { getListDocumentsQueryKey } from "~/api/default/default";
import { useRestartDocumentPipeline } from "~/api/documents/documents";
import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { useProjectParams } from "~/helpers";
import { useToast } from "~/hooks/use-toast";
import { trackEvent } from "~/utils/analytics";
import { PipelineMode, PipelineModeSelector } from "./PipelineModeSelector";

interface ReprocessCaseDialogProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  documentId: string;
  hasBookmarks: boolean;
}

export const ReprocessCaseDialog: FC<ReprocessCaseDialogProps> = ({
  open,
  setOpen,
  documentId,
  hasBookmarks,
}) => {
  const { organizationId, caseId } = useProjectParams();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { formatMessage } = useIntl();
  const [mode, setMode] = useState<PipelineMode>(
    hasBookmarks ? "USE_TOC" : "USE_SNOWFLAKE"
  );
  const [error, setError] = useState<string | null>(null);
  const { mutateAsync: restartDocumentPipeline, isPending: isLoading } =
    useRestartDocumentPipeline();

  const disabledModes = useMemo(() => {
    if (!hasBookmarks) {
      return [
        {
          mode: "USE_TOC" as PipelineMode,
          reason: formatMessage({
            id: "reprocessDocument.preserveStructure.noBookmarks",
            defaultMessage: "No bookmarks detected in source file.",
          }),
        },
      ];
    }
    return [];
  }, [hasBookmarks, formatMessage]);

  const handleSubmit = async () => {
    setError(null);

    trackEvent({
      eventName: "app/case/actions_menu/reprocess.button/click",
      properties: { caseId, documentId, mode },
    });

    try {
      // USE_TOC means preserve original structure (skipTOC: false)
      // USE_SNOWFLAKE means smart auto-categorization (skipTOC: true)
      const skipTOC = mode === "USE_SNOWFLAKE";

      await restartDocumentPipeline({
        organizationId,
        projectId: caseId,
        documentId,
        params: { skipTOC },
      });

      await queryClient.invalidateQueries({
        queryKey: getListDocumentsQueryKey(organizationId, caseId),
      });

      toast({
        title: formatMessage({ id: "success" }),
        description: formatMessage({
          id: "reprocessDocument.toast.success",
          defaultMessage: "Reprocessing started. Updates will appear shortly.",
        }),
      });

      setOpen(false);
    } catch {
      setError(
        formatMessage({
          id: "reprocessDocument.toast.error",
          defaultMessage: "Failed to start reprocessing. Please try again.",
        })
      );
    }
  };

  const handleModeChange = (selectedMode: PipelineMode) => {
    setMode(selectedMode);
    trackEvent({
      eventName: "app/reprocess_modal/option.select",
      properties: {
        caseId,
        documentId,
        selectedMode,
        hasBookmarks,
      },
    });
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>
            <FormattedMessage
              id="reprocessDocument.title"
              defaultMessage="Reprocess Document"
            />
          </DialogTitle>
          <DialogDescription>
            <FormattedMessage
              id="reprocessDocument.description"
              defaultMessage="Choose how you want Sky AI to organize this document."
            />
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          <PipelineModeSelector
            selectedMode={mode}
            onModeChange={handleModeChange}
            availableModes={["USE_TOC", "USE_SNOWFLAKE"]}
            disabledModes={disabledModes}
          />
        </div>

        {error && <div className="text-sm text-red-500 mb-2">{error}</div>}

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => setOpen(false)}
            disabled={isLoading}
          >
            <FormattedMessage id="cancel" defaultMessage="Cancel" />
          </Button>
          <Button onClick={handleSubmit} disabled={isLoading}>
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                <FormattedMessage
                  id="reprocessDocument.button.confirming"
                  defaultMessage="Reprocessing..."
                />
              </>
            ) : (
              <FormattedMessage
                id="reprocessDocument.button.confirm"
                defaultMessage="Reprocess"
              />
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
